import type { UserPublic } from '@accounts/shared';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';

import { STR } from '../../../i18n';
import { ApiError } from '../../../lib/api';
import { formatDate } from '../../../lib/format';
import {
  listLifecycleChecklists,
  reopenChecklistItem,
  tickChecklistItem,
} from '../../../lib/lifecycleApi';
import { queryKeys } from '../../../lib/queryKeys';
import { showToast } from '../../../lib/toast';
import { Callout, Card, CardHeader, Chip, EmptyRow } from '../../../ui/Primitives';
import { eyebrowStyle, monoStyle, rowActionStyle } from '../../../ui/styles';

import { hasItWork } from '../access/itEmail/checklist';
import { ChecklistItEmailDialog } from '../access/itEmail/ChecklistItEmailDialog';
import { useChecklistItEmail } from '../access/itEmail/useChecklistItEmail';

import { appDeactivationState } from './model';

const offboardingChecklistKey = (userId: string) =>
  ['admin', 'access', 'lifecycle', 'checklists', 'offboarding', userId] as const;

export function OffboardingChecklist({ user }: { user: UserPublic }) {
  const queryClient = useQueryClient();
  const [error, setError] = useState<string | null>(null);
  const itEmail = useChecklistItEmail();
  const query = useQuery({
    queryKey: offboardingChecklistKey(user.id),
    queryFn: () => listLifecycleChecklists({ userId: user.id, kind: 'offboarding' }),
  });
  const checklist = query.data?.data.find((entry) => entry.retiredAt === null) ?? null;

  function refreshed() {
    setError(null);
    void queryClient.invalidateQueries({ queryKey: offboardingChecklistKey(user.id) });
    void queryClient.invalidateQueries({ queryKey: queryKeys.accessAll });
  }

  function failed(failure: unknown) {
    setError(failure instanceof ApiError ? failure.message : STR.errors.generic);
  }

  const tick = useMutation({
    mutationFn: (id: string) => tickChecklistItem(id),
    onSuccess: (result) => {
      refreshed();
      showToast(STR.access.offboardingChecklist.done(result.item.record?.catalogItem.name ?? 'Item'));
    },
    onError: failed,
  });
  const reopen = useMutation({
    mutationFn: reopenChecklistItem,
    onSuccess: (result) => {
      refreshed();
      showToast(
        STR.access.offboardingChecklist.reopened(result.item.record?.catalogItem.name ?? 'Item'),
        'warn',
      );
    },
    onError: failed,
  });

  if (query.isLoading) return null;
  if (query.isError) {
    return (
      <Card>
        <CardHeader title={STR.access.offboardingChecklist.title} />
        <EmptyRow>{STR.access.offboardingChecklist.loadFailed}</EmptyRow>
      </Card>
    );
  }
  if (checklist === null) return null;

  const live = checklist.items.filter((item) => item.retiredAt === null);
  const outside = live.filter((item) => item.action !== 'app_deactivation');
  const apps = live.filter((item) => item.action === 'app_deactivation');
  const done = live.filter((item) => item.status === 'done').length;
  const percent = live.length === 0 ? 100 : Math.round((done / live.length) * 100);
  const busyId = tick.isPending
    ? tick.variables
    : reopen.isPending
      ? reopen.variables
      : null;

  return (
    <Card style={{ borderLeft: '3px solid var(--bg-warn)' }}>
      <CardHeader
        title={STR.access.offboardingChecklist.title}
        aside={
          <span className="tx-subtle" style={{ fontSize: 12, fontWeight: 500 }}>
            {checklist.completedAt === null
              ? STR.access.offboardingChecklist.progress(done, live.length, formatDate(checklist.createdAt))
              : STR.access.offboardingChecklist.complete}
          </span>
        }
        action={
          hasItWork(checklist) ? (
            <button
              type="button"
              className="tbtn"
              style={rowActionStyle}
              disabled={itEmail.draft.isPending}
              onClick={() => {
                itEmail.draft.mutate(checklist, { onError: failed });
              }}
            >
              {itEmail.draft.isPending ? STR.itEmails.drafting : STR.itEmails.draftItEmail}
            </button>
          ) : undefined
        }
      />
      <div style={{ height: 4, background: 'var(--track)' }}>
        <div aria-hidden="true" style={{ height: 4, width: `${String(percent)}%`, background: 'var(--bg-success)' }} />
      </div>
      {error === null ? null : (
        <div style={{ padding: '12px 18px 0' }}>
          <Callout tone="danger" role="alert">{error}</Callout>
        </div>
      )}

      {outside.length > 0 ? (
        <>
          <div className="tx-subtle" style={{ ...eyebrowStyle, padding: '9px 18px', borderBottom: '1px solid var(--border)' }}>
            {STR.access.offboardingChecklist.outsideSystems}
          </div>
          <ul style={{ listStyle: 'none', margin: 0, padding: 0 }}>
            {outside.map((item) => {
              const record = item.record;
              if (record === null) return null;
              return (
                <li key={item.id} className="row" style={{ display: 'flex', gap: 12, alignItems: 'center', padding: '11px 18px', borderBottom: '1px solid var(--border)' }}>
                  <input
                    type="checkbox"
                    checked={item.status === 'done'}
                    disabled={tick.isPending || reopen.isPending}
                    aria-label={item.status === 'done' ? STR.access.offboardingChecklist.reopen : STR.access.offboardingChecklist.markDone}
                    onChange={() => {
                      if (item.status === 'done') reopen.mutate(item.id);
                      else tick.mutate(item.id);
                    }}
                  />
                  <div style={{ minWidth: 0, flex: 1 }}>
                    <div style={{ fontSize: 13, fontWeight: 600 }}>
                      {record.catalogItem.name} · {STR.access.offboarding.action[item.action as Exclude<typeof item.action, 'set_up' | 'app_deactivation'>]}
                    </div>
                    <div className="tx-subtle" style={{ fontSize: 11.5, marginTop: 2 }}>
                      <span style={monoStyle}>{record.login ?? STR.accessCommon.noLogin}</span>
                      {item.doneAt === null ? '' : ` · ${STR.access.offboardingChecklist.doneAt(formatDate(item.doneAt))}`}
                    </div>
                  </div>
                  <button
                    type="button"
                    className="tbtn"
                    style={rowActionStyle}
                    disabled={tick.isPending || reopen.isPending}
                    onClick={() => {
                      if (item.status === 'done') reopen.mutate(item.id);
                      else tick.mutate(item.id);
                    }}
                  >
                    {busyId === item.id
                      ? STR.common.loading
                      : item.status === 'done'
                        ? STR.access.offboardingChecklist.reopen
                        : STR.access.offboardingChecklist.markDone}
                  </button>
                </li>
              );
            })}
          </ul>
        </>
      ) : null}

      {apps.length > 0 ? (
        <>
          <div className="tx-subtle" style={{ ...eyebrowStyle, padding: '9px 18px', borderBottom: '1px solid var(--border)' }}>
            {STR.access.offboardingChecklist.appDeactivations}
          </div>
          <ul style={{ listStyle: 'none', margin: 0, padding: 0 }}>
            {apps.map((item) => {
              const state = appDeactivationState(item);
              return (
                <li key={item.id} className="row" style={{ display: 'flex', gap: 12, alignItems: 'center', padding: '11px 18px', borderBottom: '1px solid var(--border)' }}>
                  <span aria-hidden="true" className={state === 'confirmed' ? 'tx-success-strong' : state === 'overdue' ? 'tx-danger-strong' : 'tx-subtle'}>
                    {state === 'confirmed' ? '✓' : '◷'}
                  </span>
                  <div style={{ minWidth: 0, flex: 1 }}>
                    <div style={{ fontSize: 13, fontWeight: 600 }}>{item.application?.name ?? STR.access.offboardingChecklist.unknownApp}</div>
                    <div className="tx-subtle" style={{ fontSize: 11.5, marginTop: 2 }}>
                      {state === 'confirmed'
                        ? STR.access.offboardingChecklist.confirmedAt(formatDate(item.deprovisioning?.confirmedAt ?? item.doneAt ?? item.createdAt))
                        : STR.access.offboardingChecklist.waitingForApp}
                    </div>
                  </div>
                  <Chip tone={state === 'confirmed' ? 'positive' : state === 'overdue' ? 'danger' : 'attention'} size="sm">
                    {STR.access.offboardingChecklist.state[state]}
                  </Chip>
                </li>
              );
            })}
          </ul>
          <div className="tx-subtle" style={{ padding: '10px 18px', fontSize: 11.5 }}>
            {STR.access.offboardingChecklist.appReadOnly}
          </div>
        </>
      ) : null}
      {live.length === 0 ? <EmptyRow>{STR.access.offboardingChecklist.empty}</EmptyRow> : null}
      {itEmail.open === null ? null : (
        <ChecklistItEmailDialog draft={itEmail.open} onClose={itEmail.close} />
      )}
    </Card>
  );
}
